import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Plus, Users, Calendar, AlertCircle, Check, Mail, Send } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const ListingCreditsManager = () => {
  const [credits, setCredits] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [durationDays, setDurationDays] = useState('30');
  const [note, setNote] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, expired

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchCredits();
    fetchTrainers();
  }, []);

  const fetchCredits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/listing-credits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCredits(data.credits || []);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/trainers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrainers(data.trainers || []);
      }
    } catch (error) {
      console.error('Error fetching trainers:', error);
    }
  };

  const handleAssignCredit = async () => {
    if (!selectedTrainer || !durationDays) {
      toast({
        title: "Error",
        description: "Please select a trainer and duration",
        variant: "destructive"
      });
      return;
    }

    setAssigning(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/listing-credits/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trainer_id: selectedTrainer,
          duration_days: parseInt(durationDays),
          note: note || undefined
        })
      });

      if (response.ok) {
        toast({
          title: "Success!",
          description: "Credit assigned successfully"
        });
        setShowAssignModal(false);
        setSelectedTrainer('');
        setDurationDays('30');
        setNote('');
        fetchCredits();
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to assign credit",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to assign credit",
        variant: "destructive"
      });
    } finally {
      setAssigning(false);
    }
  };

  const sendWarningEmails = async () => {
    setSendingEmails(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/listing-credits/send-warnings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: "Warning Emails Sent",
          description: `Sent: ${data.emails_sent}, Skipped: ${data.emails_skipped || 0}, Total expiring: ${data.total_expiring}`,
        });
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to send warning emails",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send warning emails",
        variant: "destructive"
      });
    } finally {
      setSendingEmails(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isActive = (credit) => {
    if (credit.status !== 'active') return false;
    const endDate = new Date(credit.end_date);
    const now = new Date();
    return endDate > now;
  };

  const filteredCredits = credits.filter(credit => {
    if (filterStatus === 'active') return isActive(credit);
    if (filterStatus === 'expired') return !isActive(credit);
    return true;
  });

  // Group credits by trainer
  const creditsByTrainer = {};
  filteredCredits.forEach(credit => {
    if (!creditsByTrainer[credit.trainer_id]) {
      creditsByTrainer[credit.trainer_id] = [];
    }
    creditsByTrainer[credit.trainer_id].push(credit);
  });

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold dark:text-white">Listing Credits Management</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage trainer listing credits</p>
        </div>
        <Button onClick={() => setShowAssignModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Assign Credit
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Credits</p>
                <p className="text-2xl font-bold dark:text-white">{credits.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Credits</p>
                <p className="text-2xl font-bold text-green-600">
                  {credits.filter(c => isActive(c)).length}
                </p>
              </div>
              <Check className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Expired Credits</p>
                <p className="text-2xl font-bold text-red-600">
                  {credits.filter(c => !isActive(c)).length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <Button
          variant={filterStatus === 'all' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('all')}
        >
          All ({credits.length})
        </Button>
        <Button
          variant={filterStatus === 'active' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('active')}
        >
          Active ({credits.filter(c => isActive(c)).length})
        </Button>
        <Button
          variant={filterStatus === 'expired' ? 'default' : 'outline'}
          onClick={() => setFilterStatus('expired')}
        >
          Expired ({credits.filter(c => !isActive(c)).length})
        </Button>
      </div>

      {/* Credits List */}
      <Card>
        <CardHeader>
          <CardTitle>All Listing Credits</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCredits.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No credits found
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCredits.map((credit) => (
                <div
                  key={credit.credit_id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold dark:text-white">{credit.trainer_name}</p>
                      <Badge variant={isActive(credit) ? 'default' : 'secondary'}>
                        {isActive(credit) ? 'Active' : 'Expired'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{credit.trainer_email}</p>
                    <div className="flex gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {credit.duration_days} days
                      </span>
                      <span>
                        {formatDate(credit.start_date)} - {formatDate(credit.end_date)}
                      </span>
                      <span>
                        {credit.payment_method === 'admin_grant' ? (
                          <Badge variant="outline">Admin Grant</Badge>
                        ) : (
                          <span>Paid: ${credit.amount_paid}</span>
                        )}
                      </span>
                    </div>
                    {credit.note && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Note: {credit.note}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Credit Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Assign Listing Credit</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Select Trainer</Label>
                <select
                  value={selectedTrainer}
                  onChange={(e) => setSelectedTrainer(e.target.value)}
                  className="w-full mt-1 p-2 border dark:border-gray-700 rounded-md"
                >
                  <option value="">-- Select Trainer --</option>
                  {trainers.map((trainer) => (
                    <option key={trainer.id} value={trainer.user_id || trainer.id}>
                      {trainer.name} ({trainer.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  value={durationDays}
                  onChange={(e) => setDurationDays(e.target.value)}
                  min="1"
                  placeholder="30"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Common: 30 (1 month), 90 (3 months), 180 (6 months), 365 (1 year)
                </p>
              </div>

              <div>
                <Label>Note (optional)</Label>
                <Input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g., Welcome gift"
                  className="mt-1"
                />
              </div>

              <div className="bg-blue-50 p-3 rounded text-sm text-blue-700">
                <p>This will give the trainer free listing credit for the specified duration.</p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedTrainer('');
                    setDurationDays('30');
                    setNote('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={assigning}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignCredit}
                  className="flex-1"
                  disabled={assigning}
                >
                  {assigning ? 'Assigning...' : 'Assign Credit'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ListingCreditsManager;
